import { injectable } from 'inversify'
import { defaultCadMessages } from '@/plugins/cad/module/i18n/tipKeys'

/** CAD 翻译词条的键，通常为点号分隔的命名空间字符串 */
export type CadTranslateKey = string
/** 翻译插值参数的值类型，null/undefined 表示缺省回退 */
export type CadTranslateValue = string | number | boolean | null | undefined
/** 翻译插值参数映射，键为占位符名称，值为对应替换内容 */
export type CadTranslateParams = Record<string, CadTranslateValue>
/** 翻译函数签名，接收词条 key 与可选参数，返回最终文案 */
export type CadTranslator = (key: CadTranslateKey, params?: CadTranslateParams) => string

/** 模板字符串插值 */
const interpolate = (template: string, params?: CadTranslateParams) => {
  if (!params) {
    return template
  }
  return template.replace(/\{(\w+)\}/g, (match, token) => {
    const value = params[token]
    return value === undefined || value === null ? match : String(value)
  })
}

/** 默认翻译器，使用内置中文消息 */
const defaultCadTranslator: CadTranslator = (key, params) => {
  const template = defaultCadMessages[key]
  return interpolate(template ?? key, params)
}

/** CAD 国际化服务 */
@injectable()
export class CadI18nService {
  private translator: CadTranslator = defaultCadTranslator
  private readonly listeners = new Set<() => void>()

  /**
   * 设置自定义翻译器，传 undefined 重置为默认。
   *
   * @param translator - 自定义翻译函数，省略时回退到内置中文翻译器
   */
  setTranslator(translator?: CadTranslator) {
    this.translator = translator ?? defaultCadTranslator
    this.listeners.forEach((listener) => listener())
  }

  /**
   * 翻译指定 key。
   *
   * @param key - 词条键
   * @param params - 插值参数，可选
   * @returns 翻译后的文案；词条缺失时回退为原始 key
   */
  translate(key: CadTranslateKey, params?: CadTranslateParams) {
    return this.translator(key, params)
  }

  /**
   * 订阅翻译器变更，返回取消订阅函数。
   *
   * @param listener - 翻译器更新后触发的回调
   * @returns 取消订阅的函数
   */
  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
}
